<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="d-block text-center font-weight-bold primary py-2 admin-title" style="word-break: normal">
                        CONDICIONES DEL SORTEO
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>

        <v-row justify="center">
            <v-col cols="12" lg="10">
                <v-card>
                    <v-progress-linear indeterminate fixed v-if="loading" style="top:0"/>
                    <v-card-text>
                        <v-alert type="info" outlined v-if="conditions">
                            Participan clientes que cumplan las condiciones del sorteo vigente. Monto minimo referencial:
                            <strong>{{ threshold }} USD</strong>.
                        </v-alert>

                        <v-alert type="warning" outlined v-if="user.role === 'Customer' && conditions && !conditions.active">
                            Los sorteos no se encuentran activos en este momento.
                        </v-alert>

                        <v-card outlined class="mb-4" v-if="user.role === 'Customer' && customer && conditions && conditions.active">
                            <v-card-title class="font-weight-bold pb-1" style="word-break: normal">
                                Tu participacion en la quincena vigente
                            </v-card-title>
                            <v-card-subtitle v-if="period">
                                Periodo: <strong>{{ period.label }}</strong>
                            </v-card-subtitle>
                            <v-card-text>
                                <v-alert type="success" outlined v-if="customer.winner && customer.winner.status === 'pending'">
                                    <div class="font-weight-bold mb-1">Felicitaciones, ganaste el sorteo.</div>
                                    <div>{{ customer.winner.message }}</div>
                                    <div class="mt-2">
                                        Tambien enviamos un correo de felicitacion con los detalles del premio.
                                    </div>
                                    <v-btn
                                        class="mt-4"
                                        color="primary"
                                        depressed
                                        :to="'/claims/v/' + customer.winner.claim_ticket_id"
                                        v-if="customer.winner.claim_ticket_id"
                                    >
                                        <v-icon left>mdi-chat</v-icon>
                                        Abrir chat de soporte
                                    </v-btn>
                                </v-alert>
                                <v-alert type="success" outlined v-else-if="customer.winner && customer.winner.status === 'claimed'">
                                    Tu premio figura como reclamado.
                                </v-alert>
                                <v-alert type="warning" outlined v-else-if="customer.excluded">
                                    Tu inscripcion fue retirada del sorteo de esta quincena. Comunicate con soporte si necesitas revisar el caso.
                                </v-alert>
                                <v-alert type="success" outlined v-else-if="customer.enrolled">
                                    Ya estas inscrito en el sorteo de esta quincena.
                                </v-alert>
                                <v-alert type="warning" outlined v-else-if="!customer.eligible">
                                    Para inscribirte necesitas un envio completado minimo de {{ threshold }} USD dentro de esta quincena.
                                </v-alert>
                                <v-alert type="info" outlined v-else>
                                    Tienes una operacion valida para esta quincena. Puedes inscribirte ahora.
                                </v-alert>

                                <v-row dense align="center" v-if="customer.eligible_transfer">
                                    <v-col cols="12" sm="6">
                                        Operacion valida: <strong>#{{ customer.eligible_transfer.id }}</strong>
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        Monto equivalente: <strong>{{ Number(customer.eligible_transfer.usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} USD</strong>
                                    </v-col>
                                </v-row>

                                <v-btn color="success" large depressed :loading="enrolling" :disabled="loading || enrolling || customer.enrolled || customer.excluded || !customer.eligible" @click="enroll" v-if="!customer.winner">
                                    <v-icon>mdi-ticket-confirmation</v-icon>&nbsp;&nbsp;Inscribirme en esta quincena
                                </v-btn>
                            </v-card-text>
                        </v-card>

                        <v-tabs v-model="tab" centered grow v-if="canUpdate">
                            <v-tab>Vista cliente</v-tab>
                            <v-tab>Editar condiciones</v-tab>
                        </v-tabs>

                        <v-tabs-items v-model="tab">
                            <v-tab-item>
                                <v-card flat class="lottery-public-card mt-4" v-if="conditions && (conditions.active || user.role !== 'Customer')">
                                    <div class="lottery-hero">
                                        <div>
                                            <div class="lottery-eyebrow">Sorteo quincenal</div>
                                            <h2 class="lottery-title">{{ conditions.title }}</h2>
                                            <p class="lottery-subtitle" v-if="conditions.subtitle">{{ conditions.subtitle }}</p>
                                        </div>
                                        <v-avatar size="74" class="lottery-hero-icon">
                                            <v-icon size="42">mdi-gift-open-outline</v-icon>
                                        </v-avatar>
                                    </div>

                                    <v-row dense class="lottery-summary-grid">
                                        <v-col cols="12" sm="6" md="3">
                                            <v-card outlined class="lottery-summary-card">
                                                <v-icon color="primary" size="34">mdi-laptop</v-icon>
                                                <div class="lottery-summary-label">Premio</div>
                                                <div class="lottery-summary-value">{{ conditions.prize }}</div>
                                            </v-card>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="3">
                                            <v-card outlined class="lottery-summary-card">
                                                <v-icon color="primary" size="34">mdi-calendar-range</v-icon>
                                                <div class="lottery-summary-label">Duracion</div>
                                                <div class="lottery-summary-value">{{ conditions.duration }}</div>
                                            </v-card>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="3">
                                            <v-card outlined class="lottery-summary-card">
                                                <v-icon color="primary" size="34">mdi-sync-circle</v-icon>
                                                <div class="lottery-summary-label">Frecuencia</div>
                                                <div class="lottery-summary-value">{{ conditions.frequency }}</div>
                                            </v-card>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="3">
                                            <v-card outlined class="lottery-summary-card">
                                                <v-icon color="primary" size="34">mdi-cash-check</v-icon>
                                                <div class="lottery-summary-label">Monto minimo</div>
                                                <div class="lottery-summary-value">{{ conditions.minimum_amount }}</div>
                                            </v-card>
                                        </v-col>
                                    </v-row>

                                    <div class="lottery-section-list">
                                        <article class="lottery-section" v-for="(section, index) in conditions.sections" :key="index">
                                            <div class="lottery-section-number">{{ index + 1 }}</div>
                                            <div>
                                                <h3>{{ section.title }}</h3>
                                                <p>{{ section.content }}</p>
                                            </div>
                                        </article>
                                    </div>
                                </v-card>

                                <v-skeleton-loader type="article, article" v-else/>
                            </v-tab-item>

                            <v-tab-item>
                                <v-form ref="form" @submit.prevent="submit" lazy-validation v-model="valid" class="pt-4">
                                    <v-alert :type="form.active ? 'success' : 'warning'" outlined>
                                        <v-switch v-model="form.active" inset color="success" hide-details class="mt-0" :label="form.active ? 'Sorteos activos: se muestra la seccion en clientes.' : 'Sorteos desactivados: se oculta la seccion en clientes.'"/>
                                    </v-alert>
                                    <v-row dense>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.title" label="Titulo" :rules="[rules.req('title')]"/>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.subtitle" label="Subtitulo"/>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.prize" label="Premio" :rules="[rules.req('prize')]"/>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.duration" label="Duracion" :rules="[rules.req('duration')]"/>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.frequency" label="Frecuencia" :rules="[rules.req('frequency')]"/>
                                        </v-col>
                                        <v-col cols="12" md="6">
                                            <v-text-field v-model="form.minimum_amount" label="Monto minimo visible" :rules="[rules.req('minimum_amount')]"/>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-text-field v-model.number="form.threshold_usd" type="number" min="1" step="0.01" label="Monto minimo para calculo (USD)" :rules="[rules.req('threshold_usd')]"/>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-text-field v-model="form.delivery_place" label="Lugar de entrega" :rules="[rules.req('delivery_place')]"/>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-text-field v-model.number="form.claim_wait_days" type="number" min="1" label="Dias maximos para reclamar" :rules="[rules.req('claim_wait_days')]"/>
                                        </v-col>
                                    </v-row>

                                    <v-divider class="my-4"/>
                                    <div class="d-flex align-center mb-2">
                                        <h3 class="mb-0">Secciones</h3>
                                        <v-spacer/>
                                        <v-btn color="primary" small depressed @click="addSection">
                                            <v-icon small>mdi-plus-circle</v-icon>&nbsp;&nbsp;Agregar
                                        </v-btn>
                                    </div>

                                    <v-card outlined class="mb-3" v-for="(section, index) in form.sections" :key="index">
                                        <v-card-text>
                                            <v-row dense align="center">
                                                <v-col cols="12" md="4">
                                                    <v-text-field v-model="section.title" :label="'Titulo seccion ' + (index + 1)" :rules="[rules.req('section_title_' + index)]"/>
                                                </v-col>
                                                <v-col cols="12" md="7">
                                                    <v-textarea v-model="section.content" rows="3" auto-grow label="Contenido" :rules="[rules.req('section_content_' + index)]"/>
                                                </v-col>
                                                <v-col cols="12" md="1" class="text-center">
                                                    <v-btn icon color="error" :disabled="form.sections.length === 1" @click="removeSection(index)">
                                                        <v-icon>mdi-delete</v-icon>
                                                    </v-btn>
                                                </v-col>
                                            </v-row>
                                        </v-card-text>
                                    </v-card>

                                    <v-row justify="center">
                                        <v-col cols="12" sm="6" md="4">
                                            <v-btn type="submit" block large color="success" :loading="loading" :disabled="loading || !valid">
                                                <v-icon>mdi-content-save</v-icon>&nbsp;&nbsp;Guardar condiciones
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-form>
                            </v-tab-item>
                        </v-tabs-items>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from 'axios';
import Form from '../../../helpers/form';

export default {
    name: 'Conditions',
    mixins: [Form],
    props: ['user'],
    data: () => ({
        loading: false,
        valid: true,
        tab: 0,
        threshold: 50,
        canUpdate: false,
        enrolling: false,
        period: null,
        customer: null,
        conditions: null,
        form: {
            title: '',
            subtitle: '',
            prize: '',
            duration: '',
            frequency: '',
            minimum_amount: '',
            delivery_place: '',
            threshold_usd: 50,
            claim_wait_days: 7,
            active: true,
            sections: []
        }
    }),
    mounted() {
        this.getData();
    },
    methods: {
        getData() {
            this.loading = true;
            axios.get('/api/lottery/conditions').then(res => {
                this.threshold = res.data.threshold_usd;
                this.canUpdate = res.data.can_update;
                this.period = res.data.period;
                this.customer = res.data.customer;
                this.conditions = res.data.conditions;
                this.form = this.cloneConditions(res.data.conditions);
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.loading = false;
            });
        },
        cloneConditions(conditions) {
            return JSON.parse(JSON.stringify(conditions));
        },
        addSection() {
            this.form.sections.push({ title: '', content: '' });
        },
        removeSection(index) {
            if (this.form.sections.length > 1) {
                this.form.sections.splice(index, 1);
            }
        },
        enroll() {
            this.enrolling = true;
            axios.post('/api/lottery/enroll').then(res => {
                this.customer = res.data.customer;
                if (res.data.v === 'success') {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                } else {
                    this.$toast.warning(res.data.status, {icon: 'mdi-information', timeout: 8000});
                }
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.enrolling = false;
            });
        },
        submit() {
            if (!this.$refs.form.validate()) {
                return;
            }

            this.loading = true;
            axios.post('/api/lottery/conditions', this.form).then(res => {
                if (res.data.v === 'success') {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                    this.conditions = res.data.conditions;
                    this.threshold = res.data.conditions.threshold_usd;
                    this.form = this.cloneConditions(res.data.conditions);
                    this.tab = 0;
                } else {
                    this.$toast.warning(res.data.status, {icon: 'mdi-information'});
                }
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.loading = false;
            });
        }
    }
}
</script>

<style scoped>
.admin-title {
    background-color: var(--v-primary-base) !important;
    color: var(--v-secondary-base) !important;
    text-shadow: none !important;
}

>>> .v-tab,
>>> .v-label,
>>> .v-input input,
>>> .v-input textarea {
    color: rgba(0, 0, 0, 0.82) !important;
}

>>> .v-tab--active {
    color: var(--v-primary-base) !important;
}

.lottery-public-card {
    overflow: hidden;
    border-radius: 18px;
    background: #fff;
}

.lottery-hero {
    align-items: center;
    background: var(--v-primary-base);
    color: #fff;
    display: flex;
    justify-content: space-between;
    margin: -16px -16px 22px;
    padding: 28px;
}

.lottery-eyebrow {
    color: #fff !important;
    font-size: 0.78rem;
    font-weight: 800;
    letter-spacing: 0.14em;
    opacity: 0.82;
    text-transform: uppercase;
}

.lottery-title {
    color: #fff !important;
    font-size: clamp(1.55rem, 4vw, 2.25rem);
    font-weight: 900;
    line-height: 1.1;
    margin: 6px 0;
}

.lottery-subtitle {
    color: #fff !important;
    margin: 0;
    max-width: 760px;
    opacity: 0.9;
}

.lottery-hero-icon {
    background: rgba(255, 255, 255, 0.16);
    border: 1px solid rgba(255, 255, 255, 0.28);
    flex: 0 0 auto;
}

.lottery-summary-grid {
    margin-bottom: 18px;
}

.lottery-summary-card {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 170px;
    padding: 18px;
}

.lottery-summary-label {
    color: rgba(0, 0, 0, 0.55);
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    margin-top: 14px;
    text-transform: uppercase;
}

.lottery-summary-value {
    color: rgba(0, 0, 0, 0.84);
    font-size: 0.98rem;
    font-weight: 700;
    line-height: 1.35;
    margin-top: 8px;
}

.lottery-section-list {
    display: grid;
    gap: 12px;
}

.lottery-section {
    align-items: flex-start;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 16px;
    display: grid;
    gap: 14px;
    grid-template-columns: auto 1fr;
    padding: 18px;
}

.lottery-section-number {
    align-items: center;
    background: var(--v-primary-base);
    border-radius: 14px;
    color: #fff;
    display: flex;
    font-weight: 900;
    height: 38px;
    justify-content: center;
    width: 38px;
}

.lottery-section h3 {
    color: rgba(0, 0, 0, 0.86) !important;
    font-size: 1.02rem;
    font-weight: 900;
    margin: 0 0 6px;
}

.lottery-section p {
    color: rgba(0, 0, 0, 0.72);
    line-height: 1.55;
    margin: 0;
    white-space: pre-line;
}

@media (max-width: 600px) {
    .lottery-hero {
        align-items: flex-start;
        flex-direction: column;
        gap: 16px;
        padding: 22px;
    }
}
</style>
